<script setup lang="ts">
/**
 * TryItSection — "Try it now" блок на лендинге (inbox-131).
 *
 * Простой URL → short URL flow без логина. Идёт между Hero и Features
 * на HomeView. Конверсия → создал ссылку → захотел claim'нуть → signup.
 *
 * FSM: idle → loading → success | error | rate_limited
 *  - success: показывает short URL крупно + Copy + CTA "Сократить ещё".
 *  - error: красный hint под input'ом.
 *  - rate_limited: специальный card "Подождите N секунд" + CTA signup.
 *
 * Использует LinksApi.createAnon (inbox-132) + rememberAnonLink (LS).
 *
 * Backend contract:
 *   POST /api/v1/links/anon { target_url, title? }
 *   201 → AnonLink { slug, short_url, target_url, ... expires_at? }
 *   422 → invalid URL
 *   429 → rate_limited
 */

import { computed, ref } from 'vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiInput from '@/components/ui/UiInput.vue';
import { LinksApi, ApiError, type AnonLink } from '@/api/links';
import { rememberAnonLink } from '@/lib/anonLinks';

type State = 'idle' | 'loading' | 'success' | 'error' | 'rate_limited';

const state = ref<State>('idle');
const targetUrl = ref('');
const errorMsg = ref('');
const result = ref<AnonLink | null>(null);
const copied = ref(false);

const canSubmit = computed(() => state.value !== 'loading' && targetUrl.value.trim().length > 0);

function normalize(v: string): string {
  const trimmed = v.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function validate(v: string): string {
  if (!v.trim()) return 'Введите URL';
  const norm = normalize(v);
  try {
    const u = new URL(norm);
    if (!/^https?:$/i.test(u.protocol)) return 'Только http:// и https://';
    if (!u.hostname.includes('.')) return 'Похоже на некорректный домен';
    return '';
  } catch {
    return 'Не получилось разобрать URL';
  }
}

async function submit(): Promise<void> {
  const err = validate(targetUrl.value);
  if (err) {
    errorMsg.value = err;
    state.value = 'error';
    return;
  }
  state.value = 'loading';
  errorMsg.value = '';
  try {
    const link = await LinksApi.createAnon({ target_url: normalize(targetUrl.value) });
    result.value = link;
    rememberAnonLink({
      slug: link.slug,
      short_url: link.short_url,
      target_url: link.target_url,
      created_at: link.created_at,
      expires_at: link.expires_at,
    });
    state.value = 'success';
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.status === 429) {
        state.value = 'rate_limited';
        errorMsg.value = e.message || 'Слишком много попыток. Подождите минуту или войдите.';
      } else if (e.status === 422) {
        state.value = 'error';
        errorMsg.value = e.message || 'Сервер не принял URL. Проверьте формат.';
      } else if (e.status === 401 || e.status === 403) {
        state.value = 'error';
        errorMsg.value = 'Anon-создание временно недоступно. Войдите для продолжения.';
      } else {
        state.value = 'error';
        errorMsg.value = e.message || `Ошибка ${e.status}`;
      }
    } else {
      state.value = 'error';
      errorMsg.value = 'Сеть недоступна. Попробуйте ещё раз.';
    }
  }
}

async function copyShort(): Promise<void> {
  if (!result.value) return;
  try {
    await navigator.clipboard.writeText(result.value.short_url);
    copied.value = true;
    setTimeout(() => (copied.value = false), 1800);
  } catch {
    /* clipboard unavailable */
  }
}

function reset(): void {
  state.value = 'idle';
  targetUrl.value = '';
  errorMsg.value = '';
  result.value = null;
  copied.value = false;
}

const inputError = computed(() => (state.value === 'error' ? errorMsg.value : ''));
</script>

<template>
  <section
    class="try-it"
    aria-labelledby="try-it-h"
  >
    <div class="container">
      <div class="head">
        <span class="eyebrow">Попробовать сейчас</span>
        <h2 id="try-it-h">
          Сократите ссылку за 5 секунд
        </h2>
        <p class="sub">
          Без регистрации — вставьте URL и получите короткую ссылку.
          После signup'а можно подцепить созданные ссылки в свой аккаунт.
        </p>
      </div>

      <!-- IDLE / ERROR / LOADING — same form -->
      <form
        v-if="state !== 'success' && state !== 'rate_limited'"
        class="form"
        novalidate
        @submit.prevent="submit"
      >
        <UiInput
          v-model="targetUrl"
          type="url"
          placeholder="https://example.ru/очень-длинный-адрес"
          autocomplete="off"
          :error="inputError"
        />
        <UiButton
          type="submit"
          size="lg"
          :loading="state === 'loading'"
          :disabled="!canSubmit"
        >
          Сократить
        </UiButton>
      </form>

      <!-- SUCCESS -->
      <div
        v-else-if="state === 'success' && result"
        class="result"
        role="status"
      >
        <div class="result-row">
          <div class="result-label">
            Ваша ссылка готова
          </div>
          <code class="result-url">{{ result.short_url }}</code>
        </div>
        <div class="result-actions">
          <UiButton
            variant="primary"
            @click="copyShort"
          >
            {{ copied ? '✓ Скопировано' : 'Копировать' }}
          </UiButton>
          <UiButton
            variant="outline"
            @click="reset"
          >
            Сократить ещё одну
          </UiButton>
          <a
            class="signup-link"
            href="/signup"
          >
            Войти, чтобы видеть статистику →
          </a>
        </div>
        <p
          v-if="result.expires_at"
          class="result-note"
        >
          Anon-ссылка живёт до
          <time :datetime="result.expires_at">
            {{ new Date(result.expires_at).toLocaleDateString('ru-RU') }}
          </time>.
          После signup'а она привяжется к вам и не истечёт.
        </p>
      </div>

      <!-- RATE LIMITED -->
      <div
        v-else-if="state === 'rate_limited'"
        class="rate-card"
        role="alert"
      >
        <div
          class="rate-icon"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div class="rate-body">
          <h3>Подождите минуту</h3>
          <p>
            {{ errorMsg }} Войдите — у залогиненных пользователей лимит выше.
          </p>
          <div class="rate-actions">
            <a
              class="rate-cta"
              href="/signup"
            >
              <UiButton variant="primary">
                Войти / создать аккаунт
              </UiButton>
            </a>
            <UiButton
              variant="ghost"
              @click="reset"
            >
              Назад
            </UiButton>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.try-it {
  padding: 64px 24px;
  background: var(--color-bg-card, #11161e);
  border-top: 1px solid var(--color-border, #1f2733);
  border-bottom: 1px solid var(--color-border, #1f2733);
}
.container {
  max-width: 760px;
  margin: 0 auto;
}

.head {
  text-align: center;
  margin-bottom: 28px;
}
.eyebrow {
  display: inline-block;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--color-accent-hi, #6f9eff);
  margin-bottom: 10px;
}
.head h2 {
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--color-fg, #e8edf3);
  margin: 0 0 10px;
}
.sub {
  font-size: 14.5px;
  color: var(--color-fg-muted, #8b95a5);
  line-height: 1.55;
  margin: 0;
}

.form {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
  align-items: start;
}
.form :deep(.input) {
  padding: 14px 14px;
  /* inbox-295 — 16px чтобы iOS Safari не зумил при focus. */
  font-size: 16px;
}

.result {
  background: var(--color-bg-elev, #0f1620);
  border: 1px solid var(--color-success, #3ecf8e);
  border-radius: var(--radius-card, 12px);
  padding: 20px 22px;
}
.result-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.result-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--color-fg-dim, #6b7480);
  font-weight: 600;
}
.result-url {
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 22px;
  font-weight: 700;
  color: var(--color-success, #3ecf8e);
  word-break: break-all;
  flex: 1;
  text-align: right;
  min-width: 0;
}
.result-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.signup-link {
  margin-left: auto;
  color: var(--color-accent-hi, #6f9eff);
  font-size: 13.5px;
  font-weight: 500;
}
.signup-link:hover {
  text-decoration: underline;
}
.result-note {
  margin-top: 14px;
  font-size: 12.5px;
  color: var(--color-fg-dim, #6b7480);
}

.rate-card {
  background: var(--color-bg-elev, #0f1620);
  border: 1px solid var(--color-warn, #f5a623);
  border-radius: var(--radius-card, 12px);
  padding: 20px 22px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.rate-icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: rgba(245, 166, 35, 0.12);
  color: var(--color-warn, #f5a623);
  border-radius: 50%;
}
.rate-icon svg {
  width: 20px;
  height: 20px;
}
.rate-body {
  flex: 1;
}
.rate-body h3 {
  margin: 0 0 6px;
  font-size: var(--fs-h3, 16px);
  font-weight: 600;
  color: var(--color-fg, #e8edf3);
}
.rate-body p {
  margin: 0 0 14px;
  font-size: 13.5px;
  color: var(--color-fg-muted, #8b95a5);
  line-height: 1.55;
}
.rate-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.rate-cta {
  text-decoration: none;
}

@media (max-width: 640px) {
  .try-it {
    padding: 48px 16px;
  }
  .head h2 {
    font-size: 24px;
  }
  .form {
    grid-template-columns: 1fr;
  }
  .result-url {
    text-align: left;
    font-size: 18px;
  }
  .signup-link {
    margin-left: 0;
  }
}
</style>
